import { useEffect, useRef, useCallback } from 'react';

import { StockItem } from '../../../../../../common/types/StockItemTypes';

/**
 * Message types for cross-tab communication via BroadcastChannel API
 */
export type BroadcastMessage =
  | {
      type: 'JOB_SUBMITTED';
      payload: {
        stockItem: StockItem;
        taskId: string;
      };
    }
  | {
      type: 'JOB_POLLING_UPDATE';
      payload: {
        status: string;
        jobId: number;
      };
    }
  | {
      type: 'JOB_SUCCEEDED';
      payload: {
        outputUrl: string;
        jobId: number;
        stockItemEdited: StockItem;
      };
    }
  | {
      type: 'JOB_FAILED';
      payload: {
        error: string;
        jobId?: number;
      };
    }
  | {
      type: 'CREDITS_UPDATED';
      payload: {
        newBalance: number;
      };
    };

export interface UseCrossTabSyncReturn {
  sendMessage: (message: BroadcastMessage) => void;
  isSupported: boolean;
}

const CHANNEL_NAME = 'video-editor-sync';

/**
 * Custom hook for synchronizing state across browser tabs using the Broadcast Channel API.
 *
 * This hook enables real-time communication between multiple tabs to coordinate:
 * - Video generation job submissions and status updates
 * - Credit balance changes
 * - Success/failure notifications
 *
 * @param onMessage - Callback function invoked when a message is received from another tab
 * @returns Object containing sendMessage function and isSupported flag
 *
 * @example
 * ```typescript
 * const { sendMessage, isSupported } = useCrossTabSync((message) => {
 *   if (message.type === 'JOB_SUBMITTED') {
 *     dispatch(startPolling(message.payload.stockItem));
 *   }
 * });
 *
 * // Later, when submitting a job
 * sendMessage({
 *   type: 'JOB_SUBMITTED',
 *   payload: { stockItem, taskId, userId }
 * });
 * ```
 */
export function useCrossTabSync(
  onMessage: (message: BroadcastMessage) => void
): UseCrossTabSyncReturn {
  const channelRef = useRef<BroadcastChannel | null>(null);
  const onMessageRef = useRef(onMessage);

  // Keep onMessage callback up to date without recreating the channel
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  const isSupported =
    typeof window !== 'undefined' && 'BroadcastChannel' in window;

  // Initialize BroadcastChannel and set up message listener
  useEffect(() => {
    if (!isSupported) {
      return;
    }

    // Create the broadcast channel
    const channel = new BroadcastChannel(CHANNEL_NAME);
    channelRef.current = channel;

    // Set up message handler
    channel.onmessage = (event: MessageEvent<BroadcastMessage>) => {
      try {
        const message = event.data;

        if (!message || !message.type) {
          return;
        }

        onMessageRef.current(message);
      } catch (error) {
        console.error(
          '[useCrossTabSync] Error handling received message:',
          error
        );
      }
    };

    // Handle channel errors
    channel.onmessageerror = (event: MessageEvent) => {
      console.error('[useCrossTabSync] BroadcastChannel error:', event);
    };

    // Cleanup function
    return () => {
      try {
        channel.close();
        channelRef.current = null;
      } catch (error) {
        console.error(
          '[useCrossTabSync] Error closing BroadcastChannel:',
          error
        );
      }
    };
  }, [isSupported]);

  /**
   * Sends a message to all other tabs via the broadcast channel
   */
  const sendMessage = useCallback(
    (message: BroadcastMessage) => {
      if (!channelRef.current) {
        if (isSupported) {
          console.warn(
            '[useCrossTabSync] Attempted to send message before channel was initialized'
          );
        }
        return;
      }

      try {
        channelRef.current.postMessage(message);
      } catch (error) {
        console.error(
          '[useCrossTabSync] Failed to broadcast message:',
          error,
          message
        );
      }
    },
    [isSupported]
  );

  return {
    sendMessage,
    isSupported,
  };
}
